import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { currentRole, isLivyAdmin, livyUnavailableMessage } from 'src/components/livy/access';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONSTANTS } from '@livable/saas-react-components';
import { Box, Button, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { LivyPanel } from 'src/components/livy/LivyPanel';
import { LivyAvatar } from 'src/components/livy/LivyAvatar';
import { useLivyChat } from 'src/components/livy/useLivyChat';
import { livyConfig } from 'src/lib/livyTransport';
import { LivyHistory } from 'src/components/livy/LivyHistory';
import { useSearchParams } from 'react-router-dom';
import { LivyTheme } from 'src/components/livy/LivyTheme';
import { freshToken } from 'src/lib/livyApi';

/**
 * Every surface runs on one engine and differs only by prompt and allowlist, so an admin can read
 * exactly what a customer or a visitor would be told. That is the point of previewing rather than
 * describing: the marketing surface has no account tools at all, and it should be obvious.
 */
const SURFACES = [{
  value: 'admin',
  label: 'Admin',
  help: 'Full read access across companies'
}, {
  value: 'pm',
  label: 'Customer',
  help: 'What a signed-in property manager sees, their own account only'
}, {
  value: 'marketing',
  label: 'Public',
  help: 'What a logged-out visitor sees, knowledge base only, no account data'
},
// Support was a page of its own wrapping this same component with a different prompt. It is a
// surface, so it is one here; the thread queue it used to sit beside is a work list and lives in
// the admin tabs instead.
{
  value: 'support',
  label: 'Support',
  help: 'Answers grounded in the knowledge base, drafts for a human to send'
}];
const SURFACE_STARTERS = {
  admin: [],
  pm: ['Why did my water charge go up this month?', 'Which of my properties still need bills uploaded?', 'How is my recovery rate trending?', 'What does common area deduction mean?'],
  marketing: ['What does Livable actually do?', 'How does utility billing work with AppFolio?', 'Can I bill residents for submetered utilities?', 'How do I get started?'],
  support: ['A resident says they were double charged for water', 'How do I explain a common area deduction to a resident?', 'A customer wants to cancel, what are their options?', 'What should I tell someone whose statement looks wrong?']
};
const STARTERS = ['Which properties have allocations pending completion this cycle?', 'What is our recovery rate by property this month?', 'Which utilities jumped most vs last cycle?', 'What is blocking billing from closing?'];

/**
 * Full-page Livy. Admin-gated for the initial rollout, every surface ships behind
 * LivableAdmin/LivableSuperAdmin and un-gates progressively.
 */
function LivyPageBody() {
  var _localStorage$getItem;
  const companyLabel = (_localStorage$getItem = localStorage.getItem(USER_SETTINGS_KEYS.companyName)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : undefined;
  const isAdmin = isLivyAdmin(currentRole());

  // Same store httpClient writes to on login and refresh.
  // freshToken, not the raw store: a streaming turn signs its own request and never passes
  // through the app's refresh interceptor.
  const getToken = useCallback(() => freshToken(), []);
  const context = useMemo(() => ({
    route: '/livy'
  }), []);
  const _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 2),
    searchParams = _useSearchParams2[0],
    setSearchParams = _useSearchParams2[1];
  const _useState = useState('admin'),
    _useState2 = _slicedToArray(_useState, 2),
    surface = _useState2[0],
    setSurface = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    historyOpen = _useState4[0],
    setHistoryOpen = _useState4[1];
  const config = livyConfig();
  const _useLivyChat = useLivyChat({
      // A null config means Livy is not deployed in this environment; the gate below renders first.
      config: config !== null && config !== void 0 ? config : {
        endpoint: '',
        region: '',
        identityPoolId: '',
        userPoolId: ''
      },
      getToken,
      context,
      surface
    }),
    messages = _useLivyChat.messages,
    streaming = _useLivyChat.streaming,
    error = _useLivyChat.error,
    send = _useLivyChat.send,
    stop = _useLivyChat.stop,
    reset = _useLivyChat.reset,
    retry = _useLivyChat.retry,
    resume = _useLivyChat.resume,
    conversationId = _useLivyChat.conversationId;

  // The command palette opens entity results as /livy?ask=… because nothing else deep-links a
  // record. Sent once, then stripped, so a refresh does not re-ask.
  const asked = useRef(false);
  useEffect(() => {
    const ask = searchParams.get('ask');
    // Authorisation before the request, not after the render. This effect runs above the guard
    // below, so without the role check a non-admin following a deep link fired a real turn at the
    // proxy while the page told them Livy was unavailable.
    if (!ask || asked.current || !config || !isAdmin) return;
    asked.current = true;
    setSearchParams({}, {
      replace: true
    });
    send(ask);
  }, [searchParams, setSearchParams, send, config, isAdmin]);
  if (!isAdmin || !config) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        p: '24px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, livyUnavailableMessage));
  }
  return (
    /*#__PURE__*/
    // The shell reserves PAGE_BOTTOM_PADDING under every page, which on a chat surface is dead
    // space between the composer and the window. Grow into it and pull the margin back, so the
    // transcript uses the full height instead of ending 80px short of the bottom.
    React.createElement(Stack, {
      sx: {
        height: "calc(100% + ".concat(CONSTANTS.PAGE_BOTTOM_PADDING, "px)"),
        mb: "-".concat(CONSTANTS.PAGE_BOTTOM_PADDING, "px"),
        minHeight: 0
      }
    }, /*#__PURE__*/React.createElement(Paper, {
      variant: "outlined",
      sx: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderWidth: '0 0 0 1px'
      }
    }, /*#__PURE__*/React.createElement(Box, {
      sx: {
        px: '16px',
        py: '12px',
        borderBottom: 1,
        borderColor: 'divider'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      justifyContent: "space-between",
      spacing: "12px"
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "10px",
      alignItems: "center",
      sx: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement(LivyAvatar, {
      size: 30,
      thinking: streaming
    }), /*#__PURE__*/React.createElement(Box, {
      sx: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '-.01em',
        lineHeight: 1.25
      }
    }, "Livy"), companyLabel && /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      sx: {
        fontSize: 11.5,
        color: 'text.secondary'
      }
    }, companyLabel))), /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "8px",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setHistoryOpen(true)
      // Resuming mid-stream replaces the transcript while the live stream keeps writing
      // into it, so the old conversation's tokens land in the resumed one.
      ,
      disabled: streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "History"), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: reset,
      disabled: messages.length === 0 || streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "New chat"), /*#__PURE__*/React.createElement(ToggleButtonGroup, {
      size: "small",
      exclusive: true,
      value: surface,
      onChange: (_, next) => {
        if (!next || next === surface) return;
        setSurface(next);
        // A new surface is a new conversation: carrying admin answers into a customer
        // preview would show a customer things their surface cannot reach.
        reset();
      }
    }, SURFACES.map(s => /*#__PURE__*/React.createElement(Tooltip, {
      key: s.value,
      title: s.help,
      placement: "bottom"
    }, /*#__PURE__*/React.createElement(ToggleButton, {
      value: s.value,
      sx: {
        textTransform: 'none',
        px: '11px',
        py: '3px',
        fontSize: 12,
        // Unselected is quiet because this is a preview control most sessions never
        // touch. Selected keeps the app's own white on primary: overriding it to
        // primary.main put teal text on a teal fill and the label disappeared.
        color: 'text.secondary',
        borderColor: 'divider'
      }
    }, s.label))))))), /*#__PURE__*/React.createElement(LivyPanel, {
      conversationId: conversationId,
      surface: surface,
      messages: messages,
      streaming: streaming,
      error: error,
      companyLabel: companyLabel,
      starters: surface === 'admin' ? STARTERS : SURFACE_STARTERS[surface],
      reach: surface === 'marketing' ? 'knowledge' : 'account',
      showScope: false,
      onSend: send,
      onStop: stop,
      onRetry: retry
    })), /*#__PURE__*/React.createElement(LivyHistory, {
      open: historyOpen,
      onClose: () => setHistoryOpen(false),
      onResume: (id, history) => {
        // Resuming an admin thread into a customer preview would show reach that surface lacks.
        setSurface('admin');
        resume(id, history);
      }
    }))
  );
}

/** Ripples off inside Livy: see LivyTheme. */
export function LivyPage() {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyPageBody, null));
}